<template>
  <el-table
    :data="list"
    border
    @cell-click="handle"
    style="width: 100%"
    header-row-class-name="node-list-header"
  >
    <el-table-column
      prop="node_rank"
      :label="$t('nodelist.rank')"
      fixed
      min-width="70"
    >
      <template slot="header">
        <el-tooltip
          class="item"
          effect="light"
          placement="top"
          :hide-after="3000"
        >
          <div slot="content">
            <p class="tip-content-text">{{ $t("nodelist.rankDesc") }}</p>
          </div>
          <span>
            {{ $t("nodelist.rank") }}
            <img
              src="~assets/img/table/info-circle.png"
              style="transform: translateY(2px)"
            />
          </span>
        </el-tooltip>
      </template>
      <template slot-scope="scope">
        <div class="rank-change-wrapper">
          <span>{{ scope.row.node_rank }}</span>
          <img
            v-if="scope.row.rank_change > 0"
            src="~assets/img/table/arrow-up.png"
            class="rank-change-img"
          />
          <img
            v-if="scope.row.rank_change < 0"
            src="~assets/img/table/arrow-down.png"
            class="rank-change-img"
          />
          <span v-if="scope.row.rank_change > 0" class="rank-up-num">{{
            scope.row.rank_change
          }}</span>
          <span v-if="scope.row.rank_change < 0" class="rank-down-num">{{
            0 - scope.row.rank_change
          }}</span>
        </div>
      </template>
    </el-table-column>
    <el-table-column
      prop="name"
      :label="$t('nodelist.name')"
      fixed
      min-width="150"
      align="left"
    >
      <template slot-scope="scope">
        <div class="node-name-wrapper">
          <span class="node-name">{{
            scope.row.name
              ? scope.row.name
              : "Node_" + scope.row.public_key.substr(0, 6)
          }}</span>
        </div>
      </template>
    </el-table-column>
    <el-table-column
      prop="checkmark"
      :label="$t('nodelist.checkmark')"
      min-width="120"
      align="left"
    >
      <template slot="header">
        <el-tooltip
          class="item"
          effect="light"
          placement="top"
          :hide-after="3000"
        >
          <div slot="content">
            <p class="tip-content-text">
              {{ $t("nodelist.checkdesc") }}
              <a href="https://node-docs.ont.io/node-checkmark" target="_blank"
                >https://node-docs.ont.io/node-checkmark</a
              >
            </p>
          </div>
          <span>
            {{ $t("nodelist.checkmark") }}
            <img
              src="~assets/img/table/info-circle.png"
              style="transform: translateY(2px)"
            />
          </span>
        </el-tooltip>
      </template>
      <template slot-scope="scope">
        <div class="node-check-wrapper">
          <el-tooltip
            class="item"
            effect="light"
            placement="top"
            :hide-after="3000"
            v-if="scope.row.bad_actor > 0"
          >
            <div slot="content">
              <p class="tip-content-text">{{ $t("nodelist.badActor") }}</p>
            </div>
            <img
              src="~assets/img/table/bad-actor.png"
              style="transform: translateY(2px)"
            />
          </el-tooltip>
          <el-tooltip
            class="item"
            effect="light"
            placement="top"
            :hide-after="3000"
            v-if="scope.row.fee_sharing_ratio > 0"
          >
            <div slot="content">
              <p class="tip-content-text">{{ $t("nodelist.stable") }}</p>
            </div>
            <img
              src="~assets/img/table/stable.png"
              style="transform: translateY(2px)"
            />
          </el-tooltip>
          <el-tooltip
            class="item"
            effect="light"
            placement="top"
            :hide-after="3000"
            v-if="scope.row.ontology_harbinger > 0"
          >
            <div slot="content">
              <p class="tip-content-text">{{ $t("nodelist.harbinger") }}</p>
            </div>
            <img
              src="~assets/img/table/harbinger.png"
              style="transform: translateY(2px)"
            />
          </el-tooltip>
          <el-tooltip
            class="item"
            effect="light"
            placement="top"
            :hide-after="3000"
            v-if="scope.row.risky > 0"
          >
            <div slot="content">
              <p class="tip-content-text">{{ $t("nodelist.risky") }}</p>
            </div>
            <img
              src="~assets/img/table/risk.png"
              style="transform: translateY(2px)"
            />
          </el-tooltip>
        </div>
      </template>
    </el-table-column>
    <el-table-column
      prop="current_stake"
      :label="$t('nodelist.totalstake')"
      min-width="110"
    >
      <template slot="header" slot-scope="scope">
        <div
          class="table-num-header table-num-text-right"
          @click="changeSort(scope)"
        >
          <span v-if="sortColumn === scope.column.property">
            <i :class="sort ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
          </span>
          <span v-else class="show-sort">
            <i class="el-icon-arrow-up"></i>
            <i class="el-icon-arrow-down"></i>
          </span>
          <span>{{ $t("nodelist.totalstake") }}</span>
        </div>
      </template>
      <template slot-scope="scope">
        <div class="table-num-text-right">
          {{ $HelperTools.toFinancialVal(scope.row.current_stake) }}
        </div>
      </template>
    </el-table-column>
    <el-table-column
      prop="init_pos"
      :label="$t('nodelist.initpos')"
      width="120"
    >
      <template slot="header">
        <div class="table-num-header table-num-text-right">
          <span>{{ $t("nodelist.initpos") }}</span>
        </div>
      </template>
      <template slot-scope="scope">
        <div class="table-num-text-right">
          {{ $HelperTools.toFinancialVal(scope.row.init_pos) }}
        </div>
      </template>
    </el-table-column>
    <el-table-column
      prop="node_proportion"
      :label="$t('nodelist.fee')"
      min-width="150"
    >
      <template slot="header">
        <div class="table-num-header table-num-text-right">
          <el-tooltip
            class="item"
            effect="light"
            placement="top"
            :hide-after="3000"
          >
            <div slot="content">
              <p class="tip-content-text">{{ $t("nodelist.reward1") }}</p>
            </div>
            <span>
              {{ $t("nodelist.fee") }}
              <img
                src="~assets/img/table/info-circle.png"
                style="transform: translateY(2px)"
              />
            </span>
          </el-tooltip>
        </div>
      </template>
      <template slot-scope="scope">
        <div class="table-num-text-right">
          {{ scope.row.node_proportion }} / {{ scope.row.user_proportion }}
        </div>
      </template>
    </el-table-column>
    <el-table-column
      prop="annual"
      :label="$t('nodelist.annual')"
      min-width="155"
    >
      <template slot="header" slot-scope="scope">
        <div
          class="table-num-header table-num-text-right"
          @click="changeSort(scope)"
        >
          <span v-if="sortColumn === scope.column.property">
            <i :class="sort ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
          </span>
          <span v-else class="show-sort">
            <i class="el-icon-arrow-up"></i>
            <i class="el-icon-arrow-down"></i>
          </span>
          <el-tooltip
            class="item"
            effect="light"
            placement="top"
            :hide-after="3000"
          >
            <div slot="content">
              <p class="tip-content-text">{{ $t("nodelist.annual1") }}</p>
            </div>
            <span>
              {{ $t("nodelist.annual") }}
              <img
                src="~assets/img/table/info-circle.png"
                style="transform: translateY(2px)"
              />
            </span>
          </el-tooltip>
        </div>
      </template>
      <template slot-scope="scope">
        <div class="table-num-text-right">
          {{
            scope.row.annual == "not open"
              ? $t("nodelist.notopen")
              : scope.row.annual
          }}
        </div>
      </template>
    </el-table-column>
    <el-table-column
      prop="progress"
      :label="$t('nodelist.progress')"
      min-width="150"
    >
      <template slot-scope="scope">
        <el-progress :percentage="parseInt(scope.row.progress)"></el-progress>
      </template>
    </el-table-column>
    <el-table-column
      prop="stakeprogress"
      :label="$t('nodelist.stake')"
      align="right"
      class="column-action"
      min-width="70"
    >
      <template slot-scope="scope">
        <div v-if="parseInt(scope.row.progress) < 100">
          <a class="goStake-text">Stake</a>
        </div>
      </template>
    </el-table-column>
  </el-table>
</template>

<script>
export default {
  props: {
    list: {
      type: Array,
    },
  },
  data() {
    return {
      customColors: [
        { color: "#48A3FF", percentage: 99 },
        { color: "#C2C2C2", percentage: 100 },
      ],
      sortColumn: "current_stake",
      // 默认降序
      sort: false,
    };
  },
  methods: {
    changeSort(scope) {
      const column = scope.column.property;
      console.log(column);
      if (column === this.sortColumn) {
        this.sort = !this.sort;
      }
      this.sortColumn = column;
      switch (column) {
        case "current_stake":
          if (this.sort) {
            this.list.sort(function (a, b) {
              return a.current_stake - b.current_stake;
            });
          } else {
            this.list.sort(function (a, b) {
              return b.current_stake - a.current_stake;
            });
          }
          break;
        case "annual":
          if (this.sort) {
            this.list.sort(function (a, b) {
              var x = parseFloat(a.annual);
              var y = parseFloat(b.annual);
              return x - y;
            });
          } else {
            this.list.sort(function (a, b) {
              var x = parseFloat(a.annual);
              var y = parseFloat(b.annual);
              return y - x;
            });
          }
          break;
      }
    },
    renderHeader(h, { column }) {
      return h("div", [
        h("span", column.label),
        h("i", {
          class: "el-icon-question",
          style: "color:#409eff;margin-left:5px;cursor:pointer",
          on: {
            click: this.open,
          },
        }),
      ]);
    },
    open() {
      this.$alert(this.$t("nodelist.reward"), this.$t("nodelist.fee"), {
        confirmButtonText: "OK",
      });
    },
    openDetails(row) {
      this.$router.push({
        name: "detailtype",
        params: { pk: row.public_key, address: row.address, type: "balance" },
      });
    },
    handle(row, column) {
      if (column.property == "stakeprogress") {
        if (parseInt(row.progress) < 100) {
          window.open(
            `https://node.ont.io/app/node-stake/${row.public_key}`,
            "_blank"
          );
        } else {
          this.$router.push({
            name: "detailtype",
            params: {
              pk: row.public_key,
              address: row.address,
              type: "balance",
            },
          });
        }
      } else {
        this.$router.push({
          name: "detailtype",
          params: { pk: row.public_key, address: row.address, type: "balance" },
        });
      }
    },
  },
};
</script>
<style lang="scss">
.node-list-header th {
  background-color: black;
  height: 60px;

  .cell {
    color: #fff;
    width: 100%;
  }
}
.show-sort {
  display: flex;
  flex-flow: column;
  justify-content: center;
}
.show-sort i {
  font-size: 10px;
}
.node-name-wrapper {
  display: flex;
  align-items: center;
}
.node-check-wrapper {
  display: flex;
  align-items: center;
  padding: 2px 0;
}
.node-check-wrapper i {
  margin-right: 5px;
}
.el-table--border th,
.el-table--border td {
  border-right: 0px solid #ebeef5;
}
.top49 {
  width: 62px;
  height: 20px;
  background-color: rgba(194, 194, 194, 1);
  text-align: center;
  border-radius: 10px;
  color: #fff;
  font-size: 12px;
  line-height: 20px;
}
.el-table .cell {
  word-break: break-word;
}
.hover-row td {
  background-color: #f9f9f9 !important;
}
.hover-row td:first-child::before {
  content: "";
  position: absolute;
  top: 0px;
  left: 0px;
  bottom: 0px;
  width: 2px;
  background-color: #48a3ff;
}
.cell div {
  padding-left: 0 !important;
}
.el-table th,
.el-table tr {
  cursor: pointer;
}
.lang_en .el-table th > .cell {
  font-family: NunitoSans-SemiBold, "Avenir", Helvetica, Arial, sans-serif;
}
.el-table td .el-progress {
  max-width: 120px;
  display: flex;
}
.el-table td .el-progress-bar__outer {
  border-radius: 10px !important;
}
.el-table td .el-progress-bar__inner {
  border-radius: 10px !important;
  background: linear-gradient(107.67deg, #78cdeb 0.53%, #48a3ff 100%);
}
.el-table td .cell {
  color: black;
}
.el-table__empty-text {
  background-image: url("~assets/img/table/loading.gif");
  background-size: 20px;
  background-repeat: no-repeat;
  background-position: center;
  color: #ffffff00;
}
.rank-change-wrapper {
  display: flex;
  align-items: center;
}
.rank-change-img {
  height: 10px;
  width: 10px;
  margin: 0 4px 0 8px;
}
.rank-change-text {
  transform: translate(0px, 1px);
  position: absolute;
}
.rank-up-num {
  font-size: 10px;
  font-family: NunitoSans-Regular, "Avenir", Helvetica, Arial, sans-serif;
  font-weight: 400;
  color: rgba(54, 203, 71, 1);
  line-height: 14px;
  transform: translateY(0px);
}
.rank-down-num {
  font-size: 10px;
  font-family: NunitoSans-Regular, "Avenir", Helvetica, Arial, sans-serif;
  font-weight: 400;
  color: rgba(212, 0, 0, 1);
  line-height: 14px;
  transform: translateY(0px);
}
.tip-content-text {
  max-width: 280px;
}
.cell .el-progress__text {
  display: block;
  min-width: 40px;
}
.cell .el-progress-bar {
  transform: translateY(4px);
}
.goStake-text {
  font-size: 14px;
  font-family: NunitoSans-Regular, "Avenir", Helvetica, Arial, sans-serif;
  font-weight: 400;
  color: rgba(72, 163, 255, 1) !important;
  line-height: 19px;
}
</style>
